import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { useScreenDimensions, useDevice } from "../_layout";
import {
  Text,
  View,
  ScrollView,
  Animated,
  TouchableOpacity,
  StyleSheet,
  Platform,
  KeyboardAvoidingView,
} from "react-native";
import GlobalStyles from "../../Resources/Styles";
import Colors from "../../Resources/Color";
import Fonts from "../../Resources/Fonts";
import { useTranslation } from "react-i18next";

import { observer, Observer } from "mobx-react";
import { Ionicons } from "@expo/vector-icons";
import ImageMessageManager from "../Classes/ImageMessageManager";
import ChatMessageRowView from "./ChatMessageRowView";
import { FlashList } from "@shopify/flash-list";
import ChatManager from "../Classes/ChatManager";
import { Chat, SyncStatus } from "../../Resources/Constants";
import IconEmptyView from "../Components/IconEmptyView";
import LoadingLogo from "../Components/LoadingLogo";
import ChatInputView from "./ChatInputView";
import MediaManager from "../Classes/MediaManager";
import { formatChatDate, formatDayMonth, isSameDay } from "../utils/dateUtils";

const ChatEmptyView = React.memo(() => {
  return (
    <View
      style={{
        transform: [{ scaleX: 1 }],
        flex: 1,
        flexGrow: 1,
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      <View
        style={{
          transform: [{ scaleY: -1 }],
          flex: 1,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <IconEmptyView
          iconLibrary="Ionicons"
          iconName="chatbubbles-outline"
          mainText="emptyChatTitle-string"
          subText="emptyChatText-string"
          marginTop={60}
          marginBottom={60}
        />
      </View>
    </View>
  );
});

const DateSeparator = React.memo(
  ({ date, language, marginTop = 0, marginBottom = 12 }) => {
    return (
      <View
        style={{
          alignItems: "center",
          marginTop: marginTop,
          marginBottom: marginBottom,
        }}
      >
        <View
          style={{
            backgroundColor: Colors.lightGrayColor,
            paddingHorizontal: 12,
            paddingVertical: 4,
            borderRadius: 8,
          }}
        >
          <Text
            style={{
              fontSize: 11,
              color: Colors.grayColor,
              // fontFamily: Fonts.regular,
            }}
          >
            {formatChatDate(date, language)}
          </Text>
        </View>
      </View>
    );
  }
);

const ChatView = observer(({ client, height = "auto", fullScreen = false }) => {
  // const { screenSize, windowHeight, windowWidth } = useScreenDimensions();
  // console.log("windowHeight", windowHeight);
  const { t, i18n } = useTranslation();
  const chatManager = ChatManager.getInstance();
  const mediaManager = MediaManager.getInstance();
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);

  const sortedMessages = client.messages
    .slice()
    .sort((a, b) => b.timestamp - a.timestamp);

  // Animated value for scroll position
  const scrollY = useRef(new Animated.Value(0)).current;

  // Interpolate the scrollY value so that when the user has scrolled up 100 pixels,
  const buttonTranslateY = useMemo(
    () =>
      scrollY.interpolate({
        inputRange: [400, 800],
        outputRange: [40, 0],
        extrapolate: "clamp",
      }),
    [scrollY]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      chatManager.setProperty("updatingMessagesLoading", true);
      try {
        const blocksStatus = client.syncStatusManager.operations.listenerBlocks;
        if (
          blocksStatus !== SyncStatus.SYNCED &&
          blocksStatus !== SyncStatus.WAITING
        ) {
          await client.syncStatusManager.safeOperation("listenerBlocks", () =>
            client.firestoreListener.setupBlocksListener(true)
          );
        }

        await client.syncStatusManager.safeOperation("listenerChat", () =>
          client.firestoreListener.setupChatListener(t)
        );
      } catch (error) {
        console.error("Error fetching chat data:", error);
      } finally {
        if (isMounted) {
          chatManager.setProperty("updatingMessagesLoading", false);
        }
      }
    };

    fetchData();
    client.resetUnreadCountAndMarkRead();

    return () => {
      isMounted = false;
      client?.firestoreListener?.removeChatListener();
      if (client?.coach?.selectedClientId !== client.id) {
        client.firestoreListener.removeBlocksListener();
        client.syncStatusManager.setOperationStatus("listenerBlocks", null);
      }
    };
  }, [client, chatManager]);

  useEffect(() => {
    const updateMapping = () => {
      const mapping = sortedMessages.reduce((acc, message, index) => {
        acc[message.id.toString()] = index;
        return acc;
      }, {});
      chatManager.setProperty("messageIndexes", mapping);
    };

    let fallbackTimeout;
    if (!chatManager.updatingMessagesLoading) {
      // Fallback in case interactions never finish within 300ms
      fallbackTimeout = setTimeout(() => {
        updateMapping();
      }, 700);
    }

    return () => {
      if (fallbackTimeout) clearTimeout(fallbackTimeout);
    };
  }, [sortedMessages, chatManager.updatingMessagesLoading]);

  useEffect(() => {
    const scrollEl = document.querySelector('[data-testid="chatFlashList"]');
    if (!scrollEl) return;
    const onWheel = (e) => {
      e.preventDefault();
      scrollEl.scrollTop -= e.deltaY;
    };
    scrollEl.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      scrollEl.removeEventListener("wheel", onWheel);
    };
  }, []);

  const renderItem = useCallback(
    ({ item, index }) => {
      const previousItem =
        index < sortedMessages.length - 1 ? sortedMessages[index + 1] : null;

      const nextItem = index > 0 ? sortedMessages[index - 1] : null;

      const previousSame =
        previousItem && previousItem.isFromCoach === item.isFromCoach;

      const nextSame = nextItem && nextItem.isFromCoach === item.isFromCoach;

      const isFirstMessage = index === sortedMessages.length - 1;

      // Check if we need to show a date separator
      const showDateSeparator =
        (previousItem && !isSameDay(item.timestamp, previousItem.timestamp)) ||
        !previousItem;
      return (
        <View>
          {showDateSeparator && (
            <DateSeparator
              date={item.timestamp}
              language={i18n.language || "en"}
              marginTop={
                previousItem?.isFromCoach === item?.isFromCoach
                  ? 8
                  : !previousItem
                  ? 12
                  : 0
              }
              marginBottom={!previousItem ? 0 : 12}
            />
          )}
          <ChatMessageRowView
            message={item}
            previousSame={previousSame}
            nextSame={nextSame}
            isFirstMessage={isFirstMessage}
            fullScreen={fullScreen}
          />
        </View>
      );
    },
    [sortedMessages, i18n.language]
  );

  // Stable ref callback for FlashList.
  const flashListRef = useCallback(
    (ref) => {
      chatManager.setProperty("flatListRef", ref);
    },
    [chatManager]
  );

  return (
    <View style={{ flexShrink: 1, height, backgroundColor: Colors.whiteColor }}>
      <FlashList
        testID="chatFlashList"
        ref={flashListRef}
        data={chatManager.updatingMessagesLoading ? [] : sortedMessages}
        keyExtractor={(item) => item.id.toString()}
        renderItem={renderItem}
        estimatedItemSize={220}
        scrollEnabled={!chatManager.isDragging}
        overrideItemLayout={(layout, item, index) => {
          let height;

          switch (item.type) {
            case "text":
              height = 70;
              break;
            case "image":
              height = Chat.MEDIA_HEIGHT + 26;
              break;
            case "video":
              height = Chat.MEDIA_HEIGHT + 26;
              break;
            case "audio":
              height = 44;
              break;
            default:
              console.warn("Unknown type:", item.type);
              height = 140;
          }
          if (item.replyTo) {
            height += 100;
          }

          // Add height for date separator if needed
          // Check if we need to show a date separator for this item
          const previousItem =
            index < sortedMessages.length - 1
              ? sortedMessages[index + 1]
              : null;
          const showDateSeparator =
            previousItem && !isSameDay(item.timestamp, previousItem.timestamp);
          if (showDateSeparator) {
            height += 36; // DateSeparator height
          }

          layout.size = height;
          layout.span = 1;
        }}
        keyboardShouldPersistTaps="always"
        getItemType={(item) => {
          return item.replyTo ? `${item.type}_reply` : item.type;
        }}
        initialNumToRender={20}
        maxToRenderPerBatch={20}
        windowSize={10}
        removeClippedSubviews={true}
        onScroll={(event) => {
          scrollY.setValue(event.nativeEvent.contentOffset.y);
        }}
        scrollEventThrottle={16}
        showsVerticalScrollIndicator={false}
        onEndReachedThreshold={0.05}
        inverted
        disableAutoLayout={true}
        ListFooterComponent={
          sortedMessages.length < 10 && !mediaManager.showingMediaPlayer ? (
            <View style={{ flexGrow: 1, height: 100 }} />
          ) : loadingMoreMessages ? (
            <View
              style={{
                width: "100%",
                alignItems: "center",
                justifyContent: "center",
                marginVertical: 10,
              }}
            >
              <LoadingLogo
                container={false}
                width={50}
                height={50}
                dark={true}
              />
            </View>
          ) : null
        }
        ListEmptyComponent={
          // Only show empty view when not loading and there are no messages
          chatManager.updatingMessagesLoading || sortedMessages.length === 0 ? (
            <ChatEmptyView />
          ) : null
        }
        onEndReached={async () => {
          if (sortedMessages.length > 12) {
            setLoadingMoreMessages(true);
            console.log(
              "Fetching next batch of messages, sortedMessages.length",
              sortedMessages.length
            );

            await client.fetchNextChatBatch(t);
            setLoadingMoreMessages(false);
          }
        }}
      />

      <ChatInputView
        client={client}
        fullScreen={fullScreen}
        enableTypeToFocus={fullScreen}
      />
      <Observer>
        {() => (
          <>
            {/* Animated button that scrolls the FlatList to the bottom */}
            <Animated.View
              style={{
                position: "absolute",
                bottom: chatManager.selectedReplyToId ? 104 : 64,
                alignSelf: "center",
                transform: [{ translateY: buttonTranslateY }],
                zIndex: 0,
              }}
            >
              <TouchableOpacity
                style={{
                  backgroundColor: Colors.messageLightColor,
                  borderRadius: 10,
                  paddingHorizontal: 10,
                  paddingVertical: 0,
                }}
                onPress={() => {
                  chatManager.scrollToBottom();
                }}
              >
                {/* Simple arrow pointing down; adjust styling as needed */}
                <Ionicons
                  name="chevron-down-outline"
                  size={22}
                  color={Colors.blackColor}
                />
              </TouchableOpacity>
            </Animated.View>
          </>
        )}
      </Observer>
      {chatManager.updatingMessagesLoading && (
        <View
          style={[
            StyleSheet.absoluteFillObject,
            {
              justifyContent: "center",
              alignItems: "center",
              zIndex: 1000,
              backgroundColor: Colors.whiteColor,
              bottom: 52,
            },
          ]}
        >
          <LoadingLogo container={false} dark={true} />
        </View>
      )}
    </View>
  );
});

export default ChatView;
